import {
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Network } from '@capacitor/network';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { genericRetryStrategy } from '~app-client/api/utils';
import { ApiDownService } from '~app-client/api-down/services';
import { CLIENT_ENVIRONMENT } from '~app-client/core/tokens';

@Injectable({ providedIn: 'root' })
export class ApiDownInterceptor implements HttpInterceptor {
	private readonly apiDownService = inject(ApiDownService);
	private readonly environment = inject(CLIENT_ENVIRONMENT);

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (!req.url.startsWith(this.environment.apiBaseUrl)) {
			return next.handle(req);
		}

		// Large multipart POST uploads (e.g. POST /files/) are not idempotent.
		// Retrying them on status 0 can duplicate the upload on the server:
		// the original request's body may already be fully delivered and
		// processing, while the retry sends the same payload again. Status 0
		// on these requests is almost always browser connection queuing
		// (per-host limit), not the API being down — so neither the retry
		// nor the API-down navigation applies. The error propagates to the
		// caller, which marks the chunk as failed in the upload dialog.
		// These endpoints already define their own retry strategy for
		// safe-to-retry HTTP error codes (429/502/503/504).
		if (this.isNonIdempotentUpload(req)) {
			return next.handle(req);
		}

		return next.handle(req).pipe(
			filter((event) => event instanceof HttpResponse),
			genericRetryStrategy(),
			tap({
				next: () => {
					//
				},
				error: (event: HttpResponse<unknown>) => {
					void this.handleEvent(event);
				},
			})
		);
	}

	private isNonIdempotentUpload(req: HttpRequest<unknown>): boolean {
		return req.method === 'POST' && req.url.includes('/files/') && req.body instanceof FormData;
	}

	private async handleEvent(event: HttpResponse<unknown>): Promise<void> {
		if (event.status === 0 && event.url?.startsWith(this.environment.apiBaseUrl)) {
			const network = await Network.getStatus();
			if (network.connected) {
				console.log('API DOWN - request to', event.url, ' FAILED');
				this.apiDownService.navigateToPage();
			}
		}
	}
}
